import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import MinimizeIcon from '@mui/icons-material/Minimize';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ChatIcon from '@mui/icons-material/Chat';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Chip,
  Fab,
  IconButton,
  Paper,
  TextField,
  Typography,
  Card,
  CardContent,
  CardActions,
  Divider,
  CircularProgress,
} from '@mui/material';
import useUserAuth from '../hooks/useUserAuth';
import azureClient from '../api/azureClient';
import { buildLeoCompanyContext } from '../constants/leoContext';
import { getRecentErrors } from './fetch-dev-console/src';

const LEO_COMPANY_CONTEXT = buildLeoCompanyContext();

/**
 * FloatingLeoWidget - Lower-left floating AI assistant
 * - Positioned in lower-left corner
 * - Expandable/collapsible
 * - Fixed scroll behavior: stays at top of new response, not bottom
 * - Hidden on admin pages with console widget
 * - Supports three modes: problem report, suggestion, or chat with Leo
 *
 * Usage: <FloatingLeoWidget />
 */
const FloatingLeoWidget = ({ pageContext = '', association = null }) => {
  const [expanded, setExpanded] = useState(false);
  const [xlView, setXlView] = useState(false); // XL view toggle for larger workspace
  const [mode, setMode] = useState('select'); // 'select' | 'problem' | 'suggestion' | 'chat'
  const [messages, setMessages] = useState([
    { text: "Hello! I'm Leo, your CRM assistant. How can I help you today?", sender: 'ai' },
  ]);
  const [feedbackMessages, setFeedbackMessages] = useState([]); // Separate conversation for feedback
  const [feedbackStep, setFeedbackStep] = useState(0); // Track feedback conversation step
  const [feedbackSummary, setFeedbackSummary] = useState(''); // AI-generated summary
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingAction, setEditingAction] = useState(null); // { messageIndex, actionIndex, value }

  const { currentUser: authUser } = useUserAuth();
  const currentUserName = authUser?.name || 'Unknown User';

  const refMessages = useRef(null);
  const lastMessageRef = useRef(null);
  const inputRef = useRef(null);
  const scrollLockRef = useRef(false); // Prevent auto-scroll during approvals

  const derivedContext =
    pageContext || (typeof window !== 'undefined' ? window.location.pathname : '');

  // Capture context for feedback submission
  const captureContext = () => {
    const associationMatch = window.location.pathname.match(/\/associations\/([a-f0-9-]+)/);
    return {
      page: window.location.pathname,
      route: document.title || '',
      url: window.location.href,
      browser: navigator.userAgent,
      os: navigator.platform,
      associationId: associationMatch ? associationMatch[1] : null,
    };
  };

  const findAssociationName = () => {
    if (typeof document === 'undefined') return null;
    const byName =
      document.querySelector('[data-association-name]') ||
      document.querySelector('.association-name');
    if (byName?.textContent?.trim()) return byName.textContent.trim();
    return null;
  };
  const associationName = findAssociationName();

  useEffect(() => {
    // FIXED SCROLL BEHAVIOR: Scroll to TOP of new message, not bottom
    const el = refMessages.current;
    if (!el) return;

    // Don't auto-scroll if scroll is locked (during approvals)
    if (scrollLockRef.current) {
      scrollLockRef.current = false; // Reset lock
      return;
    }

    const doScroll = () => {
      if (lastMessageRef.current && typeof lastMessageRef.current.scrollIntoView === 'function') {
        try {
          // KEY CHANGE: Use 'start' to show top of message, not 'end'
          lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        } catch {
          void 0;
        }
      }
      // Fallback: scroll to where new message starts
      if (typeof el.scrollTo === 'function') {
        const lastMsgEl = el.lastElementChild;
        if (lastMsgEl) {
          const offsetTop = lastMsgEl.offsetTop - 20; // 20px padding
          el.scrollTo({ top: offsetTop, behavior: 'smooth' });
        }
      }
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function')
      window.requestAnimationFrame(doScroll);
    else setTimeout(doScroll, 0);
  }, [messages]);

  // Auto-scroll feedback messages to bottom when new messages arrive
  useEffect(() => {
    if (mode !== 'problem' && mode !== 'suggestion') return;
    const el = refMessages.current;
    if (!el) return;

    const scrollToBottom = () => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    };

    // Small delay to ensure DOM is updated
    setTimeout(scrollToBottom, 50);
  }, [feedbackMessages, mode]);

  // Hide on admin page when console widget is present
  const isAdminPage = derivedContext.includes('/admin');
  if (isAdminPage) return null;

  // Lightweight markdown renderer
  const escapeHtml = str =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const markdownToHtml = raw => {
    if (!raw) return '';
    let s = escapeHtml(raw);

    // Code fences
    s = s.replace(/```([\s\S]*?)```/g, (m, p1) => {
      return `<pre style="background:#f5f5f5;padding:8px;border-radius:4px;overflow:auto"><code>${p1}</code></pre>`;
    });

    // Inline code
    s = s.replace(
      /`([^`]+)`/g,
      (m, p1) => `<code style="background:#eee;padding:2px 4px;border-radius:3px">${p1}</code>`
    );

    // Links [text](url)
    s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (m, p1, p2) => {
      const href = p2.replace(/"/g, '%22');
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#1976d2">${p1}</a>`;
    });

    // Bold **text**
    s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Italics *text*
    s = s.replace(/\*([^*]+)\*/g, '<em>$1</em>');

    // Lists
    const lines = s.split(/\r?\n/);
    let out = [];
    let inList = false;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const m = line.match(/^\s*([-*])\s+(.*)$/);
      if (m) {
        if (!inList) {
          inList = true;
          out.push('<ul style="margin:8px 0;padding-left:20px">');
        }
        out.push(`<li>${m[2]}</li>`);
      } else {
        if (inList) {
          inList = false;
          out.push('</ul>');
        }
        if (line.trim() === '') out.push('<br/>');
        else out.push(line);
      }
    }
    if (inList) out.push('</ul>');

    s = out.join('\n').replace(/\n/g, '<br/>');
    return s;
  };

  // Smart detection: Parse user messages and Leo responses for actionable data
  const detectActions = async (userMessage, aiResponse) => {
    const actions = [];
    const combined = `${userMessage} ${aiResponse}`;

    // Get current association data to compare against (only suggest if field needs update)
    let currentAssociation = null;
    const associationMatch = window.location.pathname.match(/\/associations\/([a-f0-9-]+)/);
    if (associationMatch) {
      try {
        const response = await azureClient.get(
          `/getById?containerId=associations&databaseId=condoshield&id=${associationMatch[1]}`
        );
        currentAssociation = response.data?.[0] || null;
      } catch (err) {
        console.warn('Could not fetch current association data:', err);
      }
    }

    // For long-form notes (like call summaries), use AI to extract structured data
    if (
      userMessage.length > 200 &&
      (userMessage.toLowerCase().includes('conversation') ||
        userMessage.toLowerCase().includes('notes:') ||
        userMessage.toLowerCase().includes('call with'))
    ) {
      try {
        // Ask AI to extract structured data AND strategic insights
        const extractionPrompt = `You are a sales assistant for CondoShield, a company that provides reserve study and financial planning services for HOAs and condominiums.

Analyze these meeting notes and extract:
1. Structured data (contacts, numbers, facts)
2. Strategic insights (buying motivations, positioning opportunities, next steps)

Return ONLY valid JSON with this exact structure:
{
  "numberOfUnits": number or null,
  "contactName": "name" or null,
  "contactRole": "role" or null,
  "yearBuilt": number or null,
  "currentMonthlyFee": number or null,
  "isSelfManaged": boolean or null (true if they manage themselves, false if they hire a management company),
  "keyPoints": ["point1", "point2", "point3"],
  "actionItems": ["action1", "action2"],
  "cleanedNotes": "professionally formatted version of notes",
  "buyingMotivations": ["motivation1", "motivation2"] (what problems are they trying to solve? what pain points mentioned?),
  "positioningStrategy": "2-3 sentence recommendation on how to position CondoShield based on their needs",
  "urgencyLevel": "high|medium|low" (based on timeline and pain points mentioned)
}

Meeting notes:
${userMessage}`;

        const apiUrl = import.meta.env.DEV
          ? 'http://localhost:4001/api/together-complete'
          : '/api/together-complete';

        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt: extractionPrompt,
            model: 'meta-llama/Llama-3.3-70B-Instruct-Turbo',
            temperature: 0.1,
            max_tokens: 1000,
          }),
        });

        const data = await res.json();
        const text = data.choices?.[0]?.message?.content || data.text || '';

        // Extract JSON from response (handle markdown code blocks)
        let jsonText = text;
        const jsonMatch = text.match(/```(?:json)?\s*(\{[\s\S]*\})\s*```/);
        if (jsonMatch) {
          jsonText = jsonMatch[1];
        }

        const extracted = JSON.parse(jsonText);

        // Create suggestions from extracted data - ONLY if field needs update

        // Number of Units - only suggest if empty or different
        if (extracted.numberOfUnits) {
          const currentValue = currentAssociation?.numberOfUnits || currentAssociation?.doors;
          const extractedValue = extracted.numberOfUnits.toString();
          if (!currentValue || currentValue.toString() !== extractedValue) {
            actions.push({
              type: 'field',
              field: 'numberOfUnits',
              label: currentValue
                ? `Update Number of Units (currently: ${currentValue})`
                : 'Number of Units/Doors',
              value: extractedValue,
              confidence: 'high',
              source: 'AI extracted from notes',
            });
          }
        }

        // Contact Role - only update role portion, not entire contact field
        if (extracted.contactRole && extracted.contactName) {
          const currentContact =
            currentAssociation?.contact || currentAssociation?.contactName || '';
          const needsUpdate = !currentContact
            .toLowerCase()
            .includes(extracted.contactRole.toLowerCase());

          if (needsUpdate) {
            // If contact exists, just update role; if not, add full contact
            const newValue = currentContact
              ? `${currentContact} - ${extracted.contactRole}`
              : `${extracted.contactName} (${extracted.contactRole})`;

            actions.push({
              type: 'field',
              field: 'contact',
              label: currentContact
                ? `Add Role to Contact (currently: "${currentContact}")`
                : 'Contact Name & Role',
              value: newValue,
              confidence: 'high',
              source: 'AI extracted from notes',
            });
          }
        }

        // Self-managed - only suggest if different or not set
        if (extracted.isSelfManaged !== null && extracted.isSelfManaged !== undefined) {
          const currentSelfManaged = currentAssociation?.selfManaged;
          const extractedValue = extracted.isSelfManaged;

          if (currentSelfManaged !== extractedValue) {
            actions.push({
              type: 'field',
              field: 'selfManaged',
              label:
                currentSelfManaged !== undefined
                  ? `Update Self Managed (currently: ${currentSelfManaged ? 'Yes' : 'No'})`
                  : 'Self Managed Status',
              value: extractedValue.toString(),
              confidence: 'high',
              source: 'AI inferred from notes',
            });
          }
        }

        // Year Built - always useful to add to notes
        if (extracted.yearBuilt) {
          actions.push({
            type: 'field',
            field: 'notes',
            label: 'Year Built (add to notes)',
            value: `Year Built: ${extracted.yearBuilt}`,
            confidence: 'medium',
            source: 'AI extracted from notes',
          });
        }

        // Monthly Fee - only suggest if empty or different
        if (extracted.currentMonthlyFee) {
          const currentRevenue = currentAssociation?.monthlyRevenue;
          const extractedValue = extracted.currentMonthlyFee.toString();

          if (!currentRevenue || currentRevenue.toString() !== extractedValue) {
            actions.push({
              type: 'field',
              field: 'monthlyRevenue',
              label: currentRevenue
                ? `Update Monthly Fee (currently: $${currentRevenue})`
                : 'Current Monthly Fee',
              value: extractedValue,
              confidence: 'medium',
              source: 'AI extracted from notes',
            });
          }
        }

        // Action items - always suggest
        if (extracted.actionItems && extracted.actionItems.length > 0) {
          extracted.actionItems.forEach(item => {
            actions.push({
              type: 'actionItem',
              label: 'Action Item',
              value: item,
              confidence: 'high',
              source: 'AI extracted from notes',
            });
          });
        }

        if (extracted.cleanedNotes && extracted.cleanedNotes.length > 100) {
          actions.push({
            type: 'note',
            label: 'Replace Notes with Cleaned Version',
            value: extracted.cleanedNotes,
            confidence: 'high',
            source: 'AI cleaned and structured',
          });
        }

        // Add strategic insights as a separate suggestion to add to notes
        if (
          extracted.buyingMotivations ||
          extracted.positioningStrategy ||
          extracted.urgencyLevel
        ) {
          let strategicNotesValue = '\n\n--- STRATEGIC INSIGHTS ---\n';

          if (extracted.buyingMotivations && extracted.buyingMotivations.length > 0) {
            strategicNotesValue += '\n🎯 Buying Motivations:\n';
            extracted.buyingMotivations.forEach(motivation => {
              strategicNotesValue += `• ${motivation}\n`;
            });
          }

          if (extracted.positioningStrategy) {
            strategicNotesValue += `\n📍 Positioning Strategy:\n${extracted.positioningStrategy}\n`;
          }

          if (extracted.urgencyLevel) {
            const urgencyEmoji =
              extracted.urgencyLevel === 'high'
                ? '🔴'
                : extracted.urgencyLevel === 'medium'
                  ? '🟡'
                  : '🟢';
            strategicNotesValue += `\n⏱️ Urgency: ${urgencyEmoji} ${extracted.urgencyLevel.toUpperCase()}\n`;
          }

          actions.push({
            type: 'field',
            field: 'notes',
            label: 'Add Strategic Insights to Notes',
            value: strategicNotesValue,
            confidence: 'high',
            source: 'AI strategic analysis',
          });
        }

        // Return actions AND strategic guidance as a special object
        return {
          actions,
          strategicGuidance: {
            buyingMotivations: extracted.buyingMotivations || [],
            positioningStrategy: extracted.positioningStrategy || null,
            urgencyLevel: extracted.urgencyLevel || 'medium',
          },
        };
      } catch (err) {
        console.error('AI extraction failed, falling back to regex:', err);
        // Fall through to regex patterns below
      }
    }

    // Fallback: Simple regex patterns for short messages
    // Detect number of units/doors
    const unitsMatch = combined.match(/(\d+)\s*(?:units?|doors?|condos?)/i);
    if (unitsMatch) {
      actions.push({
        type: 'field',
        field: 'numberOfUnits',
        label: 'Number of Units',
        value: unitsMatch[1],
        confidence: 'high',
        source: unitsMatch[0],
      });
    }

    // Detect budget/revenue amounts
    const budgetMatch = combined.match(
      /\$\s*(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)|(\d+)k?\s*(?:budget|revenue|monthly)/i
    );
    if (budgetMatch) {
      const amount = budgetMatch[1]
        ? budgetMatch[1].replace(/,/g, '')
        : budgetMatch[2] * (combined.includes('k') ? 1000 : 1);
      actions.push({
        type: 'field',
        field: 'monthlyRevenue',
        label: 'Monthly Revenue',
        value: amount,
        confidence: 'high',
        source: budgetMatch[0],
      });
    }

    // Detect dates (start date, follow-up date)
    const dateMatch = combined.match(
      /(?:start|begin|follow.?up|next meeting|schedule)[:\s]+(?:on\s+)?([A-Z][a-z]+\s+\d{1,2}(?:,?\s+\d{4})?|\d{1,2}\/\d{1,2}\/\d{2,4})/i
    );
    if (dateMatch) {
      actions.push({
        type: 'field',
        field: 'followUp',
        label: 'Follow Up Date',
        value: dateMatch[1],
        confidence: 'medium',
        source: dateMatch[0],
      });
    }

    // Detect status changes
    const statusMatch = combined.match(/(?:status|move to|update to|change to)[:\s]+([a-z\s]+)/i);
    if (statusMatch) {
      const status = statusMatch[1].trim();
      if (
        ['qualifying', 'proposal', 'negotiation', 'closing', 'win', 'lost'].some(s =>
          status.toLowerCase().includes(s)
        )
      ) {
        actions.push({
          type: 'field',
          field: 'status',
          label: 'Status',
          value: status,
          confidence: 'medium',
          source: statusMatch[0],
        });
      }
    }

    // Detect action items in text (simple patterns)
    const actionPatterns = [
      /(?:Action|TODO):\s*\*\s*([^\n]+)/gi,
      /(?:need to|should|must)\s+([^.!?\n]{15,150})/gi,
    ];

    actionPatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(combined)) !== null) {
        const actionText = match[1].trim();
        if (actionText.length > 10 && actionText.length < 200) {
          actions.push({
            type: 'actionItem',
            label: 'Action Item',
            value: actionText,
            confidence: 'medium',
            source: match[0].substring(0, 50) + '...',
          });
        }
      }
    });

    // Regex fallback returns simple structure without strategic guidance
    return { actions, strategicGuidance: null };
  };

  const handleSend = async (promptText = '') => {
    const t = (promptText || input || '').trim();
    if (!t) return;

    setMessages(prev => [...prev, { text: t, sender: 'user' }]);
    setInput('');
    setLoading(true);
    setTimeout(() => inputRef.current?.focus(), 0);

    try {
      const assocSummary = association
        ? `Association: ${association.name || association.title || ''}${association.address ? `, ${association.address}` : ''}. `
        : associationName
          ? `Association: ${associationName}. `
          : '';

      const assistantTone =
        'Be concise and focus on what the association is looking for and the unique opportunity. Use clear, plain English.';
      const companyContext = `You represent Condo Shield. Company context:\n${LEO_COMPANY_CONTEXT}`;

      const apiUrl = import.meta.env.DEV
        ? 'http://localhost:4001/api/together-complete'
        : '/api/together-complete';

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: `${assistantTone}\n${companyContext}\nContext: ${derivedContext}; ${assocSummary}\nQ: ${t}`,
          model: 'meta-llama/Llama-3.3-70B-Instruct-Turbo',
        }),
      });

      const data = await res.json();
      const text = data.choices?.[0]?.message?.content || data.text || 'No response';
      setMessages(prev => [...prev, { text, sender: 'ai' }]);

      // Detect actionable items from conversation (async AI extraction)
      const result = await detectActions(t, text);
      const actions = result?.actions || result || []; // Handle both new and old return format
      const guidance = result?.strategicGuidance || null;

      // If we have strategic guidance, show it first
      if (guidance && guidance.positioningStrategy) {
        let guidanceMessage = '🎯 **Strategic Insights**\n\n';

        if (guidance.buyingMotivations && guidance.buyingMotivations.length > 0) {
          guidanceMessage += '**Buying Motivations Detected:**\n';
          guidance.buyingMotivations.forEach(motivation => {
            guidanceMessage += `• ${motivation}\n`;
          });
          guidanceMessage += '\n';
        }

        guidanceMessage += `**Positioning Strategy:**\n${guidance.positioningStrategy}\n\n`;

        if (guidance.urgencyLevel) {
          const urgencyEmoji =
            guidance.urgencyLevel === 'high'
              ? '🔴'
              : guidance.urgencyLevel === 'medium'
                ? '🟡'
                : '🟢';
          guidanceMessage += `**Urgency:** ${urgencyEmoji} ${guidance.urgencyLevel.toUpperCase()}`;
        }

        setMessages(prev => [
          ...prev,
          {
            text: guidanceMessage,
            sender: 'ai',
            isGuidance: true,
          },
        ]);
      }

      // Then show actionable items
      if (actions.length > 0) {
        setMessages(prev => [
          ...prev,
          {
            text: '',
            sender: 'actions',
            actions: actions,
          },
        ]);
      }
    } catch (err) {
      console.error(err);
      setMessages(prev => [...prev, { text: 'Error connecting to AI', sender: 'ai' }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleApproveAction = async (action, messageIndex) => {
    // Lock scrolling to prevent jumping when adding approval messages
    scrollLockRef.current = true;

    // Get association ID from URL if on an association page
    const associationMatch = window.location.pathname.match(/\/associations\/([a-f0-9-]+)/);
    const associationId = associationMatch ? associationMatch[1] : null;

    if (!associationId) {
      // If not on an association page, just show approval message
      console.log('Not on association page, action:', action);
      setMessages(prev =>
        prev.map((msg, idx) => {
          if (idx === messageIndex && msg.sender === 'actions') {
            return {
              ...msg,
              actions: msg.actions.filter(a => a !== action),
              approved: [...(msg.approved || []), action],
            };
          }
          return msg;
        })
      );

      setMessages(prev => [
        ...prev,
        {
          text: `✅ Action approved: ${action.label} = "${action.value}". Note: Save manually - not on association detail page.`,
          sender: 'system',
        },
      ]);
      return;
    }

    try {
      // Show loading state
      setMessages(prev => [
        ...prev,
        {
          text: `⏳ Saving ${action.label}...`,
          sender: 'system',
          isLoading: true,
        },
      ]);

      // Get current association data for context
      const currentResponse = await azureClient.get(
        `/getById?containerId=associations&databaseId=condoshield&id=${associationId}`
      );
      const currentAssoc = currentResponse.data?.[0];

      // Prepare the update based on action type
      let updateData = {};

      if (action.type === 'field') {
        // For field updates, use the field name from the action
        if (action.field === 'notes') {
          // Special handling for notes - append instead of replace
          const currentNotes = currentAssoc?.notes || '';
          updateData[action.field] = currentNotes
            ? `${currentNotes}\n\n${action.value}`
            : action.value;
        } else if (action.field === 'contact') {
          // Update all contact-related fields to ensure consistency
          updateData.contact = action.value;
          updateData.contactName = action.value;
          updateData.primaryContact = action.value;
        } else if (action.field === 'selfManaged') {
          // Convert string to boolean
          updateData[action.field] = action.value === 'true';
        } else {
          updateData[action.field] = action.value;
        }
      } else if (action.type === 'note') {
        // Replace entire notes field
        updateData.notes = action.value;
      } else if (action.type === 'actionItem') {
        // Add action item to the history array (proper action item system)
        const currentHistory = currentAssoc?.history || [];
        const newHistoryEntry = {
          date: new Date().toISOString(),
          user: 'Leo AI Assistant',
          action: action.value,
          nextAction: {
            task: action.value,
            status: 'Pending',
            dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
          },
        };
        updateData.history = [newHistoryEntry, ...currentHistory];

        // Also add to notes for visibility
        const currentNotes = currentAssoc?.notes || '';
        updateData.notes = currentNotes
          ? `${currentNotes}\n\n📋 Action Item: ${action.value}`
          : `📋 Action Item: ${action.value}`;
      }

      // Add lastActivity timestamp
      updateData.lastActivity = new Date().toISOString();

      // Save to database
      await azureClient.post(
        `/update?containerId=associations&databaseId=condoshield&id=${associationId}`,
        updateData
      );

      // Remove loading message and update action state
      setMessages(prev => prev.filter(m => !m.isLoading));

      setMessages(prev =>
        prev.map((msg, idx) => {
          if (idx === messageIndex && msg.sender === 'actions') {
            return {
              ...msg,
              actions: msg.actions.filter(a => a !== action),
              approved: [...(msg.approved || []), action],
            };
          }
          return msg;
        })
      );

      // Show success message
      setMessages(prev => [
        ...prev,
        {
          text: `✅ Saved: ${action.label} = "${action.value}". Changes will appear on page refresh.`,
          sender: 'system',
        },
      ]);

      // DON'T auto-refresh - let user continue working
      // User can manually refresh when ready to see changes
    } catch (err) {
      // Remove loading message
      setMessages(prev => prev.filter(m => !m.isLoading));

      // Show error message
      setMessages(prev => [
        ...prev,
        {
          text: `❌ Error saving ${action.label}: ${err.message}`,
          sender: 'system',
        },
      ]);

      console.error('Error saving action:', err);
    }
  };

  const handleRejectAction = (action, messageIndex) => {
    // Remove the action from the message (will auto-show next one)
    setMessages(prev =>
      prev.map((msg, idx) => {
        if (idx === messageIndex && msg.sender === 'actions') {
          const remainingActions = msg.actions.filter(a => a !== action);
          return {
            ...msg,
            actions: remainingActions,
          };
        }
        return msg;
      })
    );
  };

  const handleEditAction = (action, messageIndex, actionIndex) => {
    setEditingAction({ messageIndex, actionIndex, value: action.value });
  };

  const handleSaveEdit = (messageIndex, actionIndex) => {
    if (!editingAction) return;

    // Update the action value
    setMessages(prev =>
      prev.map((msg, idx) => {
        if (idx === messageIndex && msg.sender === 'actions') {
          const updatedActions = [...msg.actions];
          updatedActions[actionIndex] = {
            ...updatedActions[actionIndex],
            value: editingAction.value,
          };
          return {
            ...msg,
            actions: updatedActions,
          };
        }
        return msg;
      })
    );

    setEditingAction(null);
  };

  const handleCancelEdit = () => {
    setEditingAction(null);
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (mode === 'problem' || mode === 'suggestion') {
        handleFeedbackSend();
      } else {
        handleSend();
      }
    }
  };

  // ==========================================
  // FEEDBACK MODE HANDLERS
  // ==========================================

  const startFeedbackMode = (feedbackType) => {
    setMode(feedbackType);
    setFeedbackMessages([]);
    setFeedbackStep(0);
    setFeedbackSummary('');
    setInput('');

    // Initial Leo question based on type
    const initialQuestion = feedbackType === 'problem'
      ? "Tell me what's happening. What did you expect vs what actually occurred?"
      : "What's your idea? I'd love to hear your suggestion for improving the app.";

    setFeedbackMessages([{ text: initialQuestion, sender: 'ai' }]);
  };

  const handleFeedbackSend = async () => {
    const userInput = input.trim();
    if (!userInput) return;

    // Add user message
    setFeedbackMessages(prev => [...prev, { text: userInput, sender: 'user' }]);
    setInput('');
    setLoading(true);

    try {
      const apiUrl = import.meta.env.DEV
        ? 'http://localhost:4001/api/together-complete'
        : '/api/together-complete';

      // Different follow-up questions based on step and type
      let prompt;
      const feedbackType = mode === 'problem' ? 'bug report' : 'feature suggestion';

      if (feedbackStep === 0) {
        // First follow-up
        if (mode === 'problem') {
          prompt = `You are helping gather a bug report. The user just described their problem:
"${userInput}"

Ask ONE short follow-up question to help understand the issue better. Choose from:
- When did this start happening?
- Does this happen every time, or only sometimes?
- What were you trying to accomplish?

Keep your response to 1-2 sentences.`;
        } else {
          prompt = `You are helping gather a feature suggestion. The user just shared their idea:
"${userInput}"

Ask ONE short follow-up question to better understand their suggestion. Choose from:
- What would this help you accomplish?
- How often would you use this feature?
- Is there a workaround you're using currently?

Keep your response to 1-2 sentences.`;
        }
      } else if (feedbackStep === 1) {
        // Generate summary after second response
        const conversation = feedbackMessages
          .map(m => `${m.sender === 'ai' ? 'Leo' : 'User'}: ${m.text}`)
          .join('\n');

        prompt = `You are summarizing a ${feedbackType}. Here's the conversation:
${conversation}
User: ${userInput}

Create a brief, clear summary (2-3 sentences) of the ${feedbackType}. Start with "Here's what I'll submit:" and end with asking if they're ready to send.`;
      }

      if (feedbackStep < 2) {
        const res = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt,
            model: 'meta-llama/Llama-3.3-70B-Instruct-Turbo',
            temperature: 0.7,
            max_tokens: 200,
          }),
        });

        const data = await res.json();
        const aiResponse = data.choices?.[0]?.message?.content || data.text || '';

        setFeedbackMessages(prev => [...prev, { text: aiResponse, sender: 'ai' }]);

        // If this is step 1, extract the summary
        if (feedbackStep === 1) {
          setFeedbackSummary(aiResponse);
          setFeedbackStep(2); // Ready for confirmation
        } else {
          setFeedbackStep(prev => prev + 1);
        }
      }
    } catch (err) {
      console.error('Error in feedback conversation:', err);
      setFeedbackMessages(prev => [
        ...prev,
        { text: 'Sorry, I had trouble processing that. Please try again.', sender: 'ai' },
      ]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const submitFeedback = async () => {
    setSubmittingFeedback(true);

    try {
      const context = captureContext();
      const consoleErrors = typeof getRecentErrors === 'function' ? getRecentErrors(5) : [];

      // Build conversation transcript
      const conversation = feedbackMessages.map(m => ({
        role: m.sender === 'ai' ? 'leo' : 'user',
        content: m.text,
      }));

      const feedbackData = {
        type: mode,
        user: currentUserName,
        summary: feedbackSummary || feedbackMessages.find(m => m.sender === 'user')?.text || '',
        conversation,
        context,
        consoleErrors,
      };

      const apiUrl = import.meta.env.DEV
        ? 'http://localhost:4001/api/feedback'
        : '/api/feedback';

      console.log('[Leo] Submitting feedback to:', apiUrl);
      console.log('[Leo] Feedback data:', feedbackData);

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackData),
      });

      if (!res.ok) {
        const errorText = await res.text();
        console.error('[Leo] Feedback submission failed:', res.status, errorText);
        throw new Error(`Failed to submit feedback: ${res.status} ${errorText}`);
      }

      const result = await res.json();
      console.log('[Leo] Feedback submitted successfully:', result.id);

      // Success - show confirmation and reset
      setFeedbackMessages(prev => [
        ...prev,
        {
          text: "Thanks! Your feedback has been submitted. The team will review it soon.",
          sender: 'ai',
        },
      ]);

      // Reset after a delay
      setTimeout(() => {
        setMode('select');
        setFeedbackMessages([]);
        setFeedbackStep(0);
        setFeedbackSummary('');
      }, 2000);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setFeedbackMessages(prev => [
        ...prev,
        {
          text: 'Sorry, there was an error submitting your feedback. Please try again.',
          sender: 'ai',
        },
      ]);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleBackToSelect = () => {
    setMode('select');
    setFeedbackMessages([]);
    setFeedbackStep(0);
    setFeedbackSummary('');
    setInput('');
  };

  // ==========================================
  // RENDER FUNCTIONS
  // ==========================================

  // Collapsed state: floating action button
  if (!expanded) {
    return (
      <Fab
        color='primary'
        aria-label='Open Leo'
        onClick={() => setExpanded(true)}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1200,
          width: 64,
          height: 64,
        }}
      >
        <SmartToyIcon sx={{ fontSize: 32 }} />
      </Fab>
    );
  }

  // Expanded state: chat interface
  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: xlView ? 'min(800px, 90vw)' : 400,
        height: xlView ? 'min(1200px, 90vh)' : 600,
        maxWidth: '90vw',
        maxHeight: '90vh',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'width 0.3s ease, height 0.3s ease',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: mode === 'problem' ? '#d32f2f' : mode === 'suggestion' ? '#ed6c02' : 'primary.main',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {(mode === 'problem' || mode === 'suggestion') && (
            <IconButton
              size='small'
              onClick={handleBackToSelect}
              sx={{ color: 'white', mr: 0.5 }}
              title='Back'
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          {mode === 'problem' ? (
            <BugReportIcon />
          ) : mode === 'suggestion' ? (
            <LightbulbIcon />
          ) : (
            <SmartToyIcon />
          )}
          <Typography variant='subtitle1' fontWeight='bold'>
            {mode === 'problem'
              ? 'Report a Problem'
              : mode === 'suggestion'
                ? 'Make a Suggestion'
                : 'Leo'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size='small'
            onClick={() => setXlView(!xlView)}
            sx={{ color: 'white' }}
            title={xlView ? 'Normal view' : 'XL view'}
          >
            {xlView ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
          </IconButton>
          <IconButton size='small' onClick={() => setExpanded(false)} sx={{ color: 'white' }}>
            <MinimizeIcon />
          </IconButton>
          <IconButton
            size='small'
            onClick={() => {
              setExpanded(false);
              setMode('select');
              setFeedbackMessages([]);
              setFeedbackStep(0);
              setFeedbackSummary('');
              setMessages([
                {
                  text: "Hello! I'm Leo, your CRM assistant. How can I help you today?",
                  sender: 'ai',
                },
              ]);
            }}
            sx={{ color: 'white', ml: 0.5 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Mode Selection Screen - Conversational */}
      {mode === 'select' && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            p: 3,
            bgcolor: '#f5f5f5',
          }}
        >
          {/* Leo's greeting message */}
          <Box
            sx={{
              bgcolor: 'white',
              p: 2,
              borderRadius: 2,
              boxShadow: 1,
              mb: 3,
            }}
          >
            <Typography variant='body1' sx={{ color: 'text.primary', lineHeight: 1.6 }}>
              Hey there! I'm <strong>Leo</strong>, your CRM assistant. How can I help you today?
            </Typography>
            <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
              You can report a problem, share an idea, or just chat with me about anything.
            </Typography>
          </Box>

          {/* Options as friendly cards */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button
              variant='outlined'
              onClick={() => startFeedbackMode('problem')}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2,
                borderColor: '#e0e0e0',
                color: 'text.primary',
                bgcolor: 'white',
                '&:hover': { bgcolor: '#ffebee', borderColor: '#d32f2f' },
              }}
            >
              <BugReportIcon sx={{ fontSize: 28, mr: 2, color: '#d32f2f' }} />
              <Box>
                <Typography variant='body1' fontWeight={600}>
                  Something's not working
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Report a bug or issue you've encountered
                </Typography>
              </Box>
            </Button>

            <Button
              variant='outlined'
              onClick={() => startFeedbackMode('suggestion')}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2,
                borderColor: '#e0e0e0',
                color: 'text.primary',
                bgcolor: 'white',
                '&:hover': { bgcolor: '#fff3e0', borderColor: '#ed6c02' },
              }}
            >
              <LightbulbIcon sx={{ fontSize: 28, mr: 2, color: '#ed6c02' }} />
              <Box>
                <Typography variant='body1' fontWeight={600}>
                  I have an idea
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Share a suggestion or feature request
                </Typography>
              </Box>
            </Button>

            <Button
              variant='outlined'
              onClick={() => setMode('chat')}
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                p: 2,
                borderColor: '#e0e0e0',
                color: 'text.primary',
                bgcolor: 'white',
                '&:hover': { bgcolor: '#e3f2fd', borderColor: 'primary.main' },
              }}
            >
              <ChatIcon sx={{ fontSize: 28, mr: 2, color: 'primary.main' }} />
              <Box>
                <Typography variant='body1' fontWeight={600}>
                  Chat with Leo
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  Ask questions about associations, get insights
                </Typography>
              </Box>
            </Button>
          </Box>
        </Box>
      )}

      {/* Feedback Conversation UI */}
      {(mode === 'problem' || mode === 'suggestion') && (
        <>
          {/* Feedback Messages */}
          <Box
            ref={refMessages}
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              bgcolor: '#f5f5f5',
            }}
          >
            {feedbackMessages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <Box
                  sx={{
                    maxWidth: '80%',
                    bgcolor: msg.sender === 'user' ? 'primary.main' : 'white',
                    color: msg.sender === 'user' ? 'white' : 'text.primary',
                    p: 1.5,
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  <Typography variant='body2'>{msg.text}</Typography>
                </Box>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 2, boxShadow: 1 }}>
                  <Typography variant='body2' color='text.secondary'>
                    Leo is thinking...
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Feedback Input or Confirmation Buttons */}
          <Box
            sx={{
              p: 1.5,
              bgcolor: 'white',
              borderTop: '1px solid #e0e0e0',
            }}
          >
            {feedbackStep === 2 ? (
              // Show Submit/Cancel buttons after summary
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant='outlined'
                  onClick={handleBackToSelect}
                  disabled={submittingFeedback}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  onClick={submitFeedback}
                  disabled={submittingFeedback}
                  sx={{ color: 'white' }}
                >
                  {submittingFeedback ? 'Submitting...' : 'Submit'}
                </Button>
              </Box>
            ) : (
              // Show input field during conversation
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  inputRef={inputRef}
                  fullWidth
                  size='small'
                  placeholder={
                    mode === 'problem' ? 'Describe the problem...' : 'Share your idea...'
                  }
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  multiline
                  maxRows={3}
                />
                <IconButton
                  color='primary'
                  onClick={handleFeedbackSend}
                  disabled={loading || !input.trim()}
                >
                  <SendIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Chat Mode - Original Leo Interface */}
      {mode === 'chat' && (
        <>
          {/* Quick action chips - shortened prompts */}
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              bgcolor: 'white',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Chip
              label='📋 Status update'
              size='small'
              onClick={() => handleSend('Status update • Opportunities • Next steps')}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
            />
            <Chip
              label='💡 Key opportunity'
              size='small'
              onClick={() => handleSend('What they want • Unique value we offer')}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
            />
            <Chip
              label='🎯 Buying signals'
              size='small'
              onClick={() => handleSend('Motivations to buy • Objections to address')}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
            />
            <Chip
              label='⚡ Next 3 actions'
              size='small'
              onClick={() => handleSend('3 concrete next actions to progress deal')}
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
            />
          </Box>

      {/* Messages area */}
      <Box
        ref={refMessages}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: '#f5f5f5',
        }}
      >
        {messages.map((msg, idx) => {
          const isLastMessage = idx === messages.length - 1;

          // Special rendering for action suggestions
          if (msg.sender === 'actions' && msg.actions && msg.actions.length > 0) {
            return (
              <Box
                key={idx}
                ref={isLastMessage ? lastMessageRef : null}
                sx={{ mb: 2, width: '100%' }}
              >
                <Card sx={{ bgcolor: '#fff3e0', border: '2px solid #ff9800' }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SmartToyIcon sx={{ mr: 1, color: '#ff9800' }} />
                        <Typography variant='subtitle2' fontWeight='bold' color='#e65100'>
                          Leo detected {msg.actions.length} suggested action
                          {msg.actions.length > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${msg.actions.length} pending`}
                        size='small'
                        color='warning'
                        sx={{ fontWeight: 'bold' }}
                      />
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    {msg.actions.map((action, actionIdx) => {
                      const isEditing =
                        editingAction?.messageIndex === idx &&
                        editingAction?.actionIndex === actionIdx;
                      const totalActions = (msg.approved?.length || 0) + msg.actions.length;
                      const currentNumber = (msg.approved?.length || 0) + actionIdx + 1;

                      return (
                        <Box
                          key={actionIdx}
                          sx={{
                            mb: 1.5,
                            p: 1,
                            bgcolor: 'white',
                            borderRadius: 1,
                            border: isEditing ? '2px solid #1976d2' : '1px solid #e0e0e0',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant='caption' color='text.secondary' display='block'>
                              {action.type === 'field'
                                ? '📝 Field Update'
                                : action.type === 'actionItem'
                                  ? '✅ Action Item'
                                  : '📄 Note'}
                            </Typography>
                            <Chip
                              label={`${currentNumber} of ${totalActions}`}
                              size='small'
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                          <Typography variant='body2' fontWeight='bold' sx={{ mt: 0.5 }}>
                            {action.label}
                          </Typography>

                          {isEditing ? (
                            <TextField
                              fullWidth
                              size='small'
                              value={editingAction.value}
                              onChange={e =>
                                setEditingAction({ ...editingAction, value: e.target.value })
                              }
                              sx={{ mt: 1 }}
                              autoFocus
                              multiline
                              maxRows={4}
                            />
                          ) : (
                            <Typography variant='body2' sx={{ mt: 0.5, fontStyle: 'italic' }}>
                              "{action.value}"
                            </Typography>
                          )}

                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{ mt: 0.5, display: 'block' }}
                          >
                            Source: {action.source}
                          </Typography>

                          <CardActions sx={{ p: 0, mt: 1, gap: 0.5 }}>
                            {isEditing ? (
                              <>
                                <Button
                                  size='small'
                                  variant='contained'
                                  color='primary'
                                  onClick={() => handleSaveEdit(idx, actionIdx)}
                                >
                                  Save
                                </Button>
                                <Button size='small' variant='outlined' onClick={handleCancelEdit}>
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size='small'
                                  variant='contained'
                                  color='success'
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleApproveAction(action, idx)}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size='small'
                                  variant='outlined'
                                  color='warning'
                                  startIcon={<EditIcon />}
                                  onClick={() => handleEditAction(action, idx, actionIdx)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size='small'
                                  variant='outlined'
                                  color='error'
                                  startIcon={<CancelIcon />}
                                  onClick={() => handleRejectAction(action, idx)}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                          </CardActions>
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              </Box>
            );
          }

          // System messages (confirmations)
          if (msg.sender === 'system') {
            return (
              <Box
                key={idx}
                ref={isLastMessage ? lastMessageRef : null}
                sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}
              >
                <Chip label={msg.text} color='success' size='small' sx={{ maxWidth: '90%' }} />
              </Box>
            );
          }

          // Regular messages
          return (
            <Box
              key={idx}
              ref={isLastMessage ? lastMessageRef : null}
              sx={{
                mb: 2,
                display: 'flex',
                justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
              }}
            >
              <Box
                sx={{
                  maxWidth: '80%',
                  bgcolor: msg.sender === 'user' ? 'primary.main' : 'white',
                  color: msg.sender === 'user' ? 'white' : 'text.primary',
                  p: 1.5,
                  borderRadius: 2,
                  boxShadow: 1,
                  ...(msg.isGuidance && {
                    border: '2px solid #1976d2',
                    bgcolor: '#e3f2fd',
                  }),
                }}
              >
                <Typography
                  variant='body2'
                  component='div'
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(msg.text) }}
                  sx={{
                    '& pre': { mt: 1, mb: 1 },
                    '& code': { fontSize: '0.9em' },
                    '& a': { textDecoration: 'underline' },
                  }}
                />
              </Box>
            </Box>
          );
        })}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Box
              sx={{
                bgcolor: 'white',
                p: 1.5,
                borderRadius: 2,
                boxShadow: 1,
              }}
            >
              <Typography variant='body2' color='text.secondary'>
                Leo is thinking...
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Input footer */}
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'white',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          gap: 1,
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          size='small'
          placeholder='Ask Leo...'
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          multiline
          maxRows={3}
        />
        <IconButton
          color='primary'
          onClick={() => handleSend()}
          disabled={loading || !input.trim()}
        >
          <SendIcon />
        </IconButton>
      </Box>
        </>
      )}
    </Paper>
  );
};

export default FloatingLeoWidget;
